<template>
  <div>
    <slot name="export-button" :is-busy="isExporting" :sidebar-id="sidebarId">
      <b-button size="sm" class="mr-1" :disabled="isBusy || itemsCount === 0" variant="info" v-b-toggle="sidebarId">
        <b-spinner v-if="isBusy || isExporting" small/>
        <font-awesome-icon icon="file-upload" class="mr-1"/>
        Экспорт
      </b-button>
    </slot>

    <portal to="app">
      <b-sidebar :id="sidebarId" class="export-sidebar" ref="sidebarExport" title="Экспорт" right shadow backdrop>
        <div class="px-1 py-1">
          <draggable v-model="localColumns" handle=".drag-handle">
            <b-list-group-item
              v-for="column in localColumns"
              :key="column.key"
              bg-variant="light"
              class="d-flex justify-content-between align-items-center text-small px-2 py-2"
            >
              <div class="d-inline-flex align-items-center">
                <span class="drag-handle text-primary mr-3">
                  <font-awesome-icon icon="bars"/>
                </span>
                {{ column.label }}
              </div>
              <b-form-checkbox
                v-model="column.visible"
                stack
                switch
                class="ml-1"
              />
            </b-list-group-item>
          </draggable>
          <br/>
          <b-button size="sm" variant="danger" @click="resetSettings">
            <font-awesome-icon icon="trash-restore" class="mr-1"/>
            Сброс
          </b-button>
          <b-button v-if="checkAllExport" size="sm" variant="secondary" @click="checkAllBoxes(false)">
            <font-awesome-icon :icon="['far', 'square']" class="mr-1"/>
            Снять все
          </b-button>
          <b-button v-else size="sm" variant="info" @click="checkAllBoxes(true)">
            <font-awesome-icon icon="check-square" class="mr-1"/>
            Выбрать все
          </b-button>
          <slot name="extra-buttons"></slot>
          <b-button size="sm" variant="success" @click="saveExportSettings()">
            <font-awesome-icon icon="save" class="mr-1"/>
            Сохранить
          </b-button>
          <b-button size="sm" variant="primary" class="mt-1" @click="exportFile()" :disabled="isExporting">
            <b-spinner v-if="isExporting" small/>
            <font-awesome-icon v-else icon="file-upload" class="mr-1"/>
            Экспортировать
          </b-button>
        </div>
      </b-sidebar>
    </portal>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import draggable from "vuedraggable";

export default {
  name: "ExportSidebar",
  components: { draggable },
  props: {
    sidebarId: { type: String, required: true },
    storageKey: { type: String, required: true },
    storageVersion: { type: Number, default: 1 },
    columns: { type: Array, required: true },
    dataToExport: { type: Array, required: false, default: () => [] },
    dataProvider: { type: Function, required: false, default: null },
    dataMapper: { type: Function, required: true },
    fileName: { type: String, required: true },
    sheetName: { type: String, default: "Лист-1" },
    isBusy: { type: Boolean, default: false },
    itemsCount: { type: Number, default: 0 }
  },
  data() {
    return {
      isExporting: false,
      localColumns: [],
      checkAllExport: true,
    };
  },
  created() {
    this.initColumns();
  },
  methods: {
    initColumns() {
      const storedSettings = this.getStoredSettings();
      this.localColumns = this.mergeColumns(this.columns, storedSettings);
      this.updateCheckAllFlag();
    },
    checkAllBoxes(flag) {
      this.localColumns.forEach(column => column.visible = flag);
    },
    updateCheckAllFlag() {
      if (this.localColumns.length === 0) {
        this.checkAllExport = false;
        return;
      }

      const selectedCount = this.localColumns.filter(c => c.visible).length;
      this.checkAllExport = selectedCount === this.localColumns.length;
    },
    mergeColumns(incomingColumns, storedSettings) {
      if (!storedSettings) {
        return JSON.parse(JSON.stringify(incomingColumns));
      }

      const incomingMap = new Map(incomingColumns.map(c => [c.key, c]));
      const processedKeys = new Set();

      const merged = storedSettings.map(storedCol => {
        const incomingCol = incomingMap.get(storedCol.key);

        if (incomingCol) {
          processedKeys.add(storedCol.key);
          return { ...incomingCol, visible: storedCol.visible };
        }

        return null;
      }).filter(Boolean);

      incomingColumns.forEach(incomingCol => {
        if (!processedKeys.has(incomingCol.key)) {
          merged.push(incomingCol);
        }
      });

      return merged;
    },
    getStoredSettings() {
      const storedSettings = localStorage.getItem(this.storageKey);

      if (!storedSettings) {
        return [];
      }

      const parsedSettings = JSON.parse(storedSettings);

      if (Array.isArray(parsedSettings) || parsedSettings.version !== this.storageVersion) {
        localStorage.removeItem(this.storageKey);
        return [];
      }

      return parsedSettings.data ? parsedSettings.data : [];
    },
    saveExportSettings() {
      const settings = {
        version: this.storageVersion,
        data: this.localColumns
      };

      localStorage.setItem(this.storageKey, JSON.stringify(settings));
      this.$bvToast.toast("Настройки экспорта сохранены.", {
        title: 'Сохранено', variant: 'success', solid: true, autoHideDelay: 3000
      });
    },
    async exportFile() {
      const selectedColumns = this.localColumns.filter(c => c.visible);

      if (selectedColumns.length === 0) {
        this.$bvToast.toast("Выберите данные для экспорта.", { title: 'Ошибка', variant: 'danger' });
        return;
      }

      this.isExporting = true;
      const itemsToExport = this.dataProvider ? await this.dataProvider(selectedColumns) : this.dataToExport;

      if (!itemsToExport) {
        this.$bvToast.toast("Данные для экспорта не найдены.", { title: 'Ошибка', variant: 'danger' });
        return;
      }

      const mappedItems = itemsToExport.map(item => this.dataMapper(item));
      const dataForSheet = mappedItems.map(item => {
        const row = {};
        selectedColumns.forEach(column => {
          row[column.label] = item[column.key];
        });
        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(dataForSheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, this.sheetName);
      XLSX.writeFile(workbook, this.fileName);
      this.isExporting = false;
    },
    async resetSettings() {
      this.$root.$emit('bv::toggle::collapse', this.sidebarId);
      const isConfirmed = await this.$bvModal.msgBoxConfirm('Вы хотите сбросить настройки экспорта? (Это также обновит текущую страницу).', {
        title: 'Сброс настроек',
        size: 'md',
        buttonSize: 'sm',
        headerBgVariant: 'danger',
        okVariant: 'danger',
        okTitle: 'Сбросить',
        cancelTitle: 'Отмена',
        footerClass: 'p-1',
        centered: true
      });

      if (!isConfirmed) return;

      localStorage.removeItem(this.storageKey);
      this.initColumns();

      this.$bvToast.toast("Настройки экспорта сброшены.", {
        title: 'Успешно',
        variant: 'warning',
        autoHideDelay: 2000,
        solid: true
      });

      setTimeout(() => {
        location.reload();
      }, 1111);
    }
  },
  watch: {
    localColumns: {
      handler() {
        this.updateCheckAllFlag();
      }, deep: true
    },
    columns: {
      handler() {
        this.initColumns();
      }, deep: true
    }
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/utilites/commonUi/ExportSidebar.vue